// Helpers for the "auto-reload on stale dynamic-import chunks after deploy"
// behavior wired up in src/main.tsx. Extracted so the logic is unit-testable
// without bootstrapping the full app.

const STALE_CHUNK_RELOAD_KEY = 'pp:stale-chunk-reload-at';
const STALE_CHUNK_RELOAD_WINDOW_MS = 30_000;

// Preload, rejection, and React boundaries can observe the same failure before
// navigation commits. This document's pending reload is distinct from the
// persisted throttle, which protects the next document against reload loops.
let reloadInProgress = false;

// The reload wipes the page before any in-page reporter can ship an event, so the
// recovery is handed to the next boot through sessionStorage instead. Kept apart
// from the throttle key above: that one must survive the boot that consumes this
// one, or the 30s throttle resets on every recovery.
const STALE_CHUNK_REPORT_KEY = 'pp:stale-chunk-reload-report';

// Safari private browsing, blocked storage, and quota-exceeded errors all throw
// from sessionStorage. We swallow those so the recovery reload still happens —
// at the cost of losing the throttle, which is acceptable since the reload itself
// is idempotent.
const readReloadStamp = (): number => {
  try {
    return Number(sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY) ?? '0');
  } catch {
    return 0;
  }
};

const writeReloadStamp = (now: number): void => {
  try {
    sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, String(now));
  } catch {
    // best-effort throttle only
  }
};

/** Where a recovery was triggered from. Reported as-is; carries no user data. */
const STALE_CHUNK_SOURCES = ['boundary', 'rejection', 'preload'] as const;
export type StaleChunkSource = (typeof STALE_CHUNK_SOURCES)[number];

/**
 * Which failure signature matched, as a stable code. The raw message embeds the
 * chunk URL, so only the classification travels into telemetry.
 *
 * The split is diagnostic: `bad_mime` means something returned HTML where a
 * module was expected (proxy, captive portal, WAF interstitial) and is never a
 * deploy race, while `fetch_failed` / `load_error` are what a chunk 404 after a
 * deploy looks like.
 */
const STALE_CHUNK_SIGNATURES = [
  'fetch_failed',
  'load_error',
  'import_failed',
  'bad_mime',
  'unknown',
] as const;
export type StaleChunkSignature = (typeof STALE_CHUNK_SIGNATURES)[number];

// Both guards read the same arrays the types are derived from, so a value added
// to either union cannot be produced by the classifier while still being
// rejected on the way back in (or vice versa).
const isStaleChunkSource = (value: unknown): value is StaleChunkSource =>
  typeof value === 'string' && (STALE_CHUNK_SOURCES as readonly string[]).includes(value);

const isStaleChunkSignature = (value: unknown): value is StaleChunkSignature =>
  typeof value === 'string' && (STALE_CHUNK_SIGNATURES as readonly string[]).includes(value);

export const classifyDynamicImportFailure = (message: string): StaleChunkSignature => {
  if (message.includes('Failed to fetch dynamically imported module')) {
    return 'fetch_failed';
  }
  if (message.includes('error loading dynamically imported module')) {
    return 'load_error';
  }
  if (message.includes('Importing a module script failed')) {
    return 'import_failed';
  }
  if (
    message.includes('Expected a JavaScript-or-Wasm module script') ||
    message.includes('Strict MIME type checking is enforced for module scripts')
  ) {
    return 'bad_mime';
  }
  return 'unknown';
};

export interface StaleChunkReport {
  readonly at: number;
  readonly source: StaleChunkSource;
  readonly signature: StaleChunkSignature;
}

const writeReport = (report: StaleChunkReport): void => {
  try {
    sessionStorage.setItem(STALE_CHUNK_REPORT_KEY, JSON.stringify(report));
  } catch {
    // best-effort handoff only — the reload still has to happen
  }
};

/**
 * Read and clear the handoff written before the last recovery reload. Returns
 * `null` when this boot was not one, which is every ordinary page load.
 *
 * Consuming (not just reading) is what keeps one recovery from being reported on
 * every subsequent boot of the same tab session.
 */
export const consumeStaleChunkReport = (): StaleChunkReport | null => {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(STALE_CHUNK_REPORT_KEY);
    sessionStorage.removeItem(STALE_CHUNK_REPORT_KEY);
  } catch {
    return null;
  }
  if (raw === null) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) {
      return null;
    }
    // Membership, not just `typeof string`. sessionStorage is writable by any
    // same-origin script and by hand in devtools, and both fields become Datadog
    // facet values — an open string admits unbounded facet cardinality and
    // whatever text the writer chose. A closed set is the only shape that keeps
    // the declared return type honest.
    const { at, source, signature } = parsed as Record<string, unknown>;
    if (typeof at !== 'number' || !Number.isFinite(at)) {
      return null;
    }
    if (!isStaleChunkSource(source) || !isStaleChunkSignature(signature)) {
      return null;
    }
    return { at, source, signature };
  } catch {
    return null;
  }
};

/**
 * Returns whether a reload is in progress. Callers that paint UI need the
 * distinction: a throttled call leaves the page as-is, so a caller that assumed
 * a reload was coming would sit on a blank recovery state forever.
 */
export const reloadForStaleChunk = (
  source: StaleChunkSource = 'boundary',
  signature: StaleChunkSignature = 'unknown',
): boolean => {
  if (reloadInProgress) {
    return true;
  }
  const now = Date.now();
  if (now - readReloadStamp() < STALE_CHUNK_RELOAD_WINDOW_MS) {
    return false;
  }
  writeReloadStamp(now);
  writeReport({ at: now, source, signature });
  globalThis.location.reload();
  reloadInProgress = true;
  return true;
};

/**
 * Derived from the classifier rather than carrying its own copy of the
 * signature list: the Sentry drop-list exemption and the recovery both gate on
 * this, so a signature added to one matcher and not the other would silently
 * leave that failure mode unreported while still reloading (or the reverse).
 */
export const isDynamicImportFailure = (message: string): boolean =>
  classifyDynamicImportFailure(message) !== 'unknown';

export const extractRejectionMessage = (reason: unknown): string => {
  if (typeof reason === 'string') {
    return reason;
  }
  if (typeof reason === 'object' && reason !== null && 'message' in reason) {
    const { message } = reason;
    return typeof message === 'string' ? message : '';
  }
  return '';
};

const extractRejectionStack = (reason: unknown): string => {
  if (typeof reason === 'object' && reason !== null && 'stack' in reason) {
    const { stack } = reason;
    return typeof stack === 'string' ? stack : '';
  }
  return '';
};

const EXTENSION_NOISE_FRAGMENTS = [
  'message channel closed',
  'A listener indicated an asynchronous response',
  'Extension context invalidated',
  'Could not establish connection',
];

export const isExtensionNoise = (message: string): boolean =>
  EXTENSION_NOISE_FRAGMENTS.some((fragment) => message.includes(fragment));

export const isPdfWorkerTermination = (reason: unknown, message: string): boolean => {
  if (message !== 'Worker was terminated') {
    return false;
  }
  const stack = extractRejectionStack(reason);
  return stack.includes('pdf.worker') || stack.includes('ensureNotTerminated');
};

/**
 * Stale-chunk recovery is a PRODUCTION concern: after a deploy, an open tab can
 * 404 on a dynamic import whose hashed chunk no longer exists, and a one-shot
 * reload fetches the fresh index. Under `vite dev` there are no stale deploy
 * chunks — the only dynamic-import failures are Vite's transient dependency
 * re-optimization (a 504 "Outdated Optimize Dep"), which Vite self-heals with its
 * own reload. So opt out of recovery in dev, letting genuine dev import errors
 * stay visible in the console.
 */
export const staleChunkRecoveryEnabled = (): boolean => !import.meta.env.DEV;

export const handleVitePreloadError = (_event: Event): void => {
  if (!staleChunkRecoveryEnabled()) {
    return;
  }
  reloadForStaleChunk('preload');
};

export const handleUnhandledRejection = (event: PromiseRejectionEvent): void => {
  const message = extractRejectionMessage(event.reason);
  // Extension noise is suppressed in every environment — password managers / ad
  // blockers leak these rejections locally too, and they're never actionable.
  if (isExtensionNoise(message)) {
    event.preventDefault();
    return;
  }
  if (isPdfWorkerTermination(event.reason, message)) {
    event.preventDefault();
    return;
  }
  if (isDynamicImportFailure(message)) {
    if (!staleChunkRecoveryEnabled()) {
      return;
    }
    event.preventDefault();
    reloadForStaleChunk('rejection', classifyDynamicImportFailure(message));
  }
};
